/**
 * Acknowledgement to Client - Filing step view
 * Figma: node-id=97-2397
 * Pixel-perfect: Frame 1000005753 — Acknowledgement Details + Application Details
 * Inter 16px/12px, #222222, #6D6D6D, #EEEEEE, #F7F7F7
 */
import { useState, useEffect } from "react";
import { HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import DateTimePicker from "../../components/DateTimePicker";
import { SelectField } from "../../components/ui/select";
import {
  getAcknowledgementForm,
  getAcknowledgementSummary,
  submitAcknowledgement,
  updateAcknowledgement,
  generateAcknowledgementLetter,
  generateAcknowledgementMergedLetterWithStatus,
  saveAcknowledgementPdfKey,
  uploadAcknowledgementPdf,
  sendAcknowledgementMergedLetterToClient,
  previewSendAcknowledgementMergedLetterToClient,
  sendAcknowledgementTrademarksReceiptToClient,
  previewSendAcknowledgementTrademarksReceiptToClient,
  getLeadDetails,
  type AcknowledgementFormData,
  type AcknowledgementSummary,
  type AcknowledgementApplicationItem,
  type AcknowledgementEmailPreview,
} from "../../api/discussion";
import { toast } from "sonner";
import { formatPhoneInput, validateMobileNumber } from "../../utils/phone";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import FilePreviewModal from "../../components/FilePreviewModal";
import ConfirmMessageModal from "../../components/ConfirmMessageModal";
import {
  WhatsAppMessageBlock,
  EmailPreviewBlock,
} from "../../components/InvoiceMessagePreview";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FORM_STYLES = {
  fontFamily: "Inter, sans-serif",
  fontFeatureSettings: "'ss01' on, 'cv01' on",
} as const;

const LABEL = "text-[#222222] text-[12px] md:text-xs leading-5 font-normal";
const INPUT =
  "w-full min-h-[44px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const INPUT_GHOST =
  "min-h-[44px] md:h-9 px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#6D6D6D] bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px]";

function parseDDMMYYYYToDate(s: string | null | undefined): Date | null {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(/[-\/]/).map(Number);
  if (parts.length < 3) return null;
  const [d, m, y] = parts;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function formatDateToISO(date: Date | null): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const FORM_OF_ADDRESS_OPTIONS = [
  { value: "Sir", label: "Sir" },
  { value: "Madam", label: "Madam" },
];

const APPLICANT_TYPE_LABELS: Record<string, string> = {
  SOLE_PROPRIETORSHIP: "Sole Proprietorship",
  TRUST: "Trust",
  PARTNERSHIP_FIRM: "Partnership Firm",
  SOCIETY: "Society",
  PRIVATE_LIMITED_COMPANY: "Private Limited Company",
  LIMITED_LIABILITY_PARTNERSHIP: "Limited Liability Partnership",
  OTHERS: "Others",
};

function formatApplicantType(value: string | null | undefined): string {
  if (!value) return "";
  return APPLICANT_TYPE_LABELS[value] ?? value;
}

/** Read-only row for the data-preview panel — label on the left, value on the right. */
function PreviewRow({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string | number | null | undefined;
  multiline?: boolean;
}) {
  const display =
    value === null || value === undefined || String(value).trim() === ""
      ? "—"
      : String(value);
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
      <span className="shrink-0 text-[12px] text-[#6D6D6D] sm:w-44 md:text-xs">
        {label}
      </span>
      <span
        className={`min-w-0 flex-1 text-[12px] text-[#222222] md:text-xs ${
          multiline ? "whitespace-pre-line" : "break-words"
        }`}
      >
        {display}
      </span>
    </div>
  );
}

type AcknowledgementToClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function AcknowledgementToClientView({
  leadId,
  onSuccess,
}: AcknowledgementToClientViewProps) {
  const [formData, setFormData] = useState<AcknowledgementFormData | null>(
    null
  );
  const [summary, setSummary] = useState<AcknowledgementSummary | null>(null);

  const [ackNo, setAckNo] = useState("");
  const [dateOfFiling, setDateOfFiling] = useState<Date | null>(null);
  const [formOfAddress, setFormOfAddress] = useState("");
  const [districtOfApplicant, setDistrictOfApplicant] = useState("");
  const [applications, setApplications] = useState<
    { tmNo: string; mark: string; classNumber: number; typeOfMark: string }[]
  >([]);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [sendingMerged, setSendingMerged] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [whatsAppSuccess, setWhatsAppSuccess] = useState<string | null>(null);

  // Merged Letter send (WhatsApp + Email) — confirm preview before sending.
  const [showMergedConfirm, setShowMergedConfirm] = useState(false);
  const [mergedPreviewLoading, setMergedPreviewLoading] = useState(false);
  const [mergedWaMessage, setMergedWaMessage] = useState("");
  const [mergedEmailPreview, setMergedEmailPreview] =
    useState<AcknowledgementEmailPreview | null>(null);

  // Trademarks Receipt send (WhatsApp + Email) — confirm preview before sending.
  const [showReceiptConfirm, setShowReceiptConfirm] = useState(false);
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const [receiptWaMessage, setReceiptWaMessage] = useState("");
  const [receiptEmailPreview, setReceiptEmailPreview] =
    useState<AcknowledgementEmailPreview | null>(null);

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"data" | "pdf">("data");
  const [merging, setMerging] = useState(false);

  const [statusUploading, setStatusUploading] = useState<
    Record<string, boolean>
  >({});
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [letterUploading, setLetterUploading] = useState(false);
  const [filePreview, setFilePreview] = useState<{
    url: string;
    name: string;
  } | null>(null);

  /**
   * Initialize the two "(Auto)" fields (Form of Address, District of Applicant) from filing
   * details. Called only when there is NO saved summary — when a summary exists, applySummary
   * is the source of truth and we must not let stale-closure reads of `summary` overwrite it.
   */
  const applyFormData = (fd: AcknowledgementFormData) => {
    const gender = fd.filingDetails?.signatoryGender?.toLowerCase() ?? "";
    const defaultForm = gender.startsWith("m")
      ? "Sir"
      : gender.startsWith("f")
      ? "Madam"
      : "";
    setFormOfAddress(defaultForm);
    setDistrictOfApplicant(fd.filingDetails?.applicantAddressDistrict ?? "");
  };

  const applySummary = (s: AcknowledgementSummary) => {
    setAckNo(s.acknowledgementNo ?? "");
    setDateOfFiling(parseDDMMYYYYToDate(s.dateOfFiling));
    setFormOfAddress(s.formOfAddress ?? "");
    setDistrictOfApplicant(s.districtOfApplicant ?? "");
    setApplications(
      (s.applications ?? []).map((a) => ({
        tmNo: a.tmNo ?? "",
        mark: a.mark ?? "",
        classNumber: a.classNumber ?? 0,
        typeOfMark: a.typeOfMark ?? "",
      }))
    );
  };

  /** Returns applications array normalized to exactly noOfApplications (pad or trim) so save validation passes. */
  const normalizeApplicationsToFormCount = (
    currentApps: {
      tmNo: string;
      mark: string;
      classNumber: number;
      typeOfMark: string;
    }[],
    fd: AcknowledgementFormData
  ): {
    tmNo: string;
    mark: string;
    classNumber: number;
    typeOfMark: string;
  }[] => {
    const n = fd.noOfApplications;
    const marks = fd.marks ?? [];
    const defaultMark = fd.markName ?? "";
    const defaultClass = fd.selectedClasses?.[0] ?? marks[0]?.classNumber ?? 0;
    const defaultType =
      fd.selectedTypesOfMark?.[0] ?? marks[0]?.typeOfMark ?? "";
    const result: {
      tmNo: string;
      mark: string;
      classNumber: number;
      typeOfMark: string;
    }[] = [];
    for (let i = 0; i < n; i++) {
      const prev = currentApps[i];
      const markForIdx = marks[i];
      result.push(
        prev
          ? { ...prev }
          : {
              tmNo: "",
              mark: defaultMark,
              classNumber: markForIdx?.classNumber ?? defaultClass,
              typeOfMark: markForIdx?.typeOfMark ?? defaultType,
            }
      );
    }
    return result;
  };

  const initApplicationsFromForm = (fd: AcknowledgementFormData) => {
    const n = fd.noOfApplications;
    const existing = [...applications];
    const marks = fd.marks ?? [];
    const defaultMark = fd.markName ?? "";
    const defaultClass = fd.selectedClasses?.[0] ?? marks[0]?.classNumber ?? 0;
    const defaultType =
      fd.selectedTypesOfMark?.[0] ?? marks[0]?.typeOfMark ?? "";

    const newApps: {
      tmNo: string;
      mark: string;
      classNumber: number;
      typeOfMark: string;
    }[] = [];
    for (let i = 0; i < n; i++) {
      const prev = existing[i];
      const markForIdx = marks[i];
      newApps.push({
        tmNo: prev?.tmNo ?? "",
        mark: prev?.mark ?? defaultMark,
        classNumber:
          prev?.classNumber ?? markForIdx?.classNumber ?? defaultClass,
        typeOfMark: prev?.typeOfMark ?? markForIdx?.typeOfMark ?? defaultType,
      });
    }
    setApplications(newApps);
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getAcknowledgementForm(leadId),
      getAcknowledgementSummary(leadId),
    ])
      .then(([formRes, summaryRes]) => {
        if (cancelled) return;
        setFormData(formRes);
        setSummary(summaryRes);
        if (summaryRes) {
          // Saved acknowledgement exists — applySummary is the source of truth for the
          // editable "(Auto)" fields. Don't call applyFormData here, otherwise its stale-closure
          // read of `summary` would overwrite the just-set values with filing-details defaults.
          applySummary(summaryRes);
          // Ensure applications length matches noOfApplications (pad or trim) so save validation passes
          const summaryApps = (summaryRes.applications ?? []).map((a) => ({
            tmNo: a.tmNo ?? "",
            mark: a.mark ?? "",
            classNumber: a.classNumber ?? 0,
            typeOfMark: a.typeOfMark ?? "",
          }));
          setApplications(
            normalizeApplicationsToFormCount(summaryApps, formRes)
          );
          // If the saved record happened to be missing the auto fields (legacy rows), fall
          // back to filing-details defaults for the affected ones only.
          if (!summaryRes.formOfAddress) {
            const gender =
              formRes.filingDetails?.signatoryGender?.toLowerCase() ?? "";
            setFormOfAddress(
              gender.startsWith("m")
                ? "Sir"
                : gender.startsWith("f")
                ? "Madam"
                : ""
            );
          }
          if (!summaryRes.districtOfApplicant) {
            setDistrictOfApplicant(
              formRes.filingDetails?.applicantAddressDistrict ?? ""
            );
          }
        } else {
          applyFormData(formRes);
          initApplicationsFromForm(formRes);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load acknowledgement"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    getLeadDetails(leadId)
      .then(({ lead }) => {
        if (lead?.phone) setPhoneNumber(lead.phone);
      })
      .catch(() => {});
  }, [leadId]);

  /** Invalidate the cached PDF preview when the user edits any field that feeds the letter — forces
   *  the PDF tab to re-save + re-generate on next open instead of showing the previously cached URL. */
  const invalidatePdfPreview = () => {
    setPreviewPdfUrl(null);
    setNumPages(null);
  };

  const updateApplication = (
    index: number,
    field: keyof AcknowledgementApplicationItem,
    value: string | number
  ) => {
    setApplications((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    invalidatePdfPreview();
  };

  /**
   * Persist the form. `advance` controls whether the parent's onSuccess is fired
   * (which navigates to the next filing step). Default true keeps the form's
   * native submit handler — i.e. the parent's "Save & Continue" — behaving as before.
   * Pass `advance: false` for in-page saves (the new "Save" button) so the user can
   * persist data, then keep working on this step (Preview, document uploads, send-to-client).
   */
  const handleSave = async (
    options: { advance?: boolean } = {}
  ): Promise<AcknowledgementSummary | null> => {
    const { advance = true } = options;
    if (!leadId || !formData || saving) return null;
    if (!dateOfFiling) {
      toast.error("Date of Filing is required");
      return null;
    }
    const invalid = applications.some((a) => !a.tmNo?.trim());
    if (invalid) {
      toast.error("TM Application No. is required for all applications");
      return null;
    }
    const seenTmNos = new Set<string>();
    const duplicateTmNos = new Set<string>();
    for (const a of applications) {
      const t = a.tmNo.trim();
      if (!t) continue;
      if (seenTmNos.has(t)) duplicateTmNos.add(t);
      else seenTmNos.add(t);
    }
    if (duplicateTmNos.size > 0) {
      toast.error(
        `Duplicate TM Application No.: ${[...duplicateTmNos].join(
          ", "
        )}. Each TM No. must be unique.`
      );
      return null;
    }
    if (applications.length !== formData.noOfApplications) {
      toast.error(
        `Number of applications must be ${formData.noOfApplications}`
      );
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        dateOfFiling: formatDateToISO(dateOfFiling),
        applications: applications.map((a) => ({
          tmNo: a.tmNo.trim(),
          mark: a.mark || undefined,
          classNumber: a.classNumber || undefined,
          typeOfMark: a.typeOfMark || undefined,
        })),
        formOfAddress: formOfAddress || undefined,
        districtOfApplicant: districtOfApplicant || undefined,
      };
      const result = summary
        ? await updateAcknowledgement(leadId, summary.id, payload)
        : await submitAcknowledgement(leadId, payload);
      setSummary(result);
      applySummary(result);
      toast.success("Acknowledgement saved successfully.");
      if (advance) onSuccess?.(leadId);
      return result;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
      return null;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Generate (or regenerate) the Acknowledgement Letter PDF and refresh the summary.
   * Always auto-saves first so any unsaved form edits (e.g. a changed Date of Filing) make
   * it to the DB before generation — otherwise the PDF would render against stale DB data.
   */
  const handleGeneratePdf =
    async (): Promise<AcknowledgementSummary | null> => {
      if (!leadId) return null;
      const currentSummary = await handleSave({ advance: false });
      if (!currentSummary) return null;
      setGeneratingPdf(true);
      try {
        await generateAcknowledgementLetter(leadId);
        const updated = await getAcknowledgementSummary(leadId);
        if (updated) {
          setSummary(updated);
          applySummary(updated);
        }
        toast.success("Acknowledgement Letter PDF generated");
        return updated ?? currentSummary;
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to generate PDF"
        );
        return null;
      } finally {
        setGeneratingPdf(false);
      }
    };

  const handleDownloadPdf = async () => {
    const url = summary?.acknowledgementLetterPdfUrl;
    if (!url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadFileFromUrlWithFallback(
        url,
        `Acknowledgement - ${formData?.markName ?? "Mark"}.pdf`
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const refreshSummary = async () => {
    if (!leadId) return;
    const updated = await getAcknowledgementSummary(leadId);
    if (updated) {
      setSummary(updated);
      applySummary(updated);
    }
  };

  const handleUploadStatusPdf = async (
    ackApplicationId: string,
    tmNo: string,
    file: File
  ) => {
    if (!leadId || !ackApplicationId) return;
    setStatusUploading((p) => ({ ...p, [ackApplicationId]: true }));
    try {
      const key = await uploadAcknowledgementPdf(
        file,
        `acknowledgement/${leadId}/application-status`
      );
      await saveAcknowledgementPdfKey(leadId, {
        type: "application_status",
        url: key,
        ackApplicationId,
      });
      await refreshSummary();
      toast.success(`Application Status PDF uploaded for ${tmNo}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setStatusUploading((p) => ({ ...p, [ackApplicationId]: false }));
    }
  };

  const handleUploadTrademarksReceipt = async (file: File) => {
    if (!leadId) return;
    setReceiptUploading(true);
    try {
      const key = await uploadAcknowledgementPdf(
        file,
        `acknowledgement/${leadId}/trademarks-receipt`
      );
      await saveAcknowledgementPdfKey(leadId, {
        type: "trademarks_receipt",
        url: key,
      });
      await refreshSummary();
      toast.success("Trademarks Receipt uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setReceiptUploading(false);
    }
  };

  const handleUploadMergedLetter = async (file: File) => {
    if (!leadId) return;
    setLetterUploading(true);
    try {
      const key = await uploadAcknowledgementPdf(
        file,
        `acknowledgement/${leadId}/merged-letter`
      );
      await saveAcknowledgementPdfKey(leadId, {
        type: "merged_letter",
        url: key,
      });
      await refreshSummary();
      toast.success("Acknowledgement Letter uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setLetterUploading(false);
    }
  };

  const handleMergeWithStatus = async () => {
    if (!leadId) return;
    setMerging(true);
    try {
      const result = await generateAcknowledgementMergedLetterWithStatus(
        leadId
      );
      await refreshSummary();
      if (result.downloadUrl)
        setFilePreview({
          url: result.downloadUrl,
          name: "Merged Letter + Status PDF.pdf",
        });
      toast.success("Merged Letter + Status PDF generated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to merge PDFs");
    } finally {
      setMerging(false);
    }
  };

  /**
   * Open the confirm-send preview for the Merged Letter (combined Acknowledgement Letter +
   * Application Status PDF). WhatsApp is the primary channel (so we gate on a valid phone),
   * and the combined PDF is also emailed. We fetch the exact WhatsApp + Email content first so
   * the user confirms what actually goes out.
   */
  const handleSendMergedLetter = async () => {
    const err = validateMobileNumber(phoneNumber);
    if (err) {
      toast.error(err);
      return;
    }
    if (!leadId) return;
    setMergedPreviewLoading(true);
    setShowMergedConfirm(true);
    try {
      const preview =
        await previewSendAcknowledgementMergedLetterToClient(leadId);
      setMergedWaMessage(preview.whatsappMessage ?? "");
      setMergedEmailPreview(preview.emailPreview ?? null);
    } catch (err) {
      setShowMergedConfirm(false);
      toast.error(
        err instanceof Error ? err.message : "Failed to load send preview"
      );
    } finally {
      setMergedPreviewLoading(false);
    }
  };

  /** Send the Merged Letter over WhatsApp + Email after the user confirms the preview. */
  const handleConfirmSendMergedLetter = async () => {
    if (!leadId) return;
    setWhatsAppSuccess(null);
    setSendingMerged(true);
    try {
      const result = await sendAcknowledgementMergedLetterToClient(leadId);
      if (result.emailError) {
        const msg = result.email
          ? `Merged letter sent via WhatsApp, but email to ${result.email} failed: ${result.emailError}`
          : `Merged letter sent via WhatsApp, but email failed: ${result.emailError}`;
        setWhatsAppSuccess("Merged letter sent to client via WhatsApp");
        toast.warning(msg);
      } else if (result.email) {
        setWhatsAppSuccess(
          "Merged letter sent to client via WhatsApp and Email"
        );
        toast.success("Merged letter sent to client via WhatsApp and Email");
      } else {
        setWhatsAppSuccess("Merged letter sent to client via WhatsApp");
        toast.success(
          "Merged letter sent to client via WhatsApp (no email on file)"
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSendingMerged(false);
    }
  };

  /**
   * Open the confirm-send preview for the Trademarks Receipt. WhatsApp is the primary channel
   * (so we still gate on a valid phone), and the receipt is also emailed to the client. We fetch
   * the exact WhatsApp + Email content first so the user confirms what actually goes out.
   */
  const handleSendTrademarksReceipt = async () => {
    const err = validateMobileNumber(phoneNumber);
    if (err) {
      toast.error(err);
      return;
    }
    if (!leadId) return;
    setReceiptPreviewLoading(true);
    setShowReceiptConfirm(true);
    try {
      const preview =
        await previewSendAcknowledgementTrademarksReceiptToClient(leadId);
      setReceiptWaMessage(preview.whatsappMessage ?? "");
      setReceiptEmailPreview(preview.emailPreview ?? null);
    } catch (err) {
      setShowReceiptConfirm(false);
      toast.error(
        err instanceof Error ? err.message : "Failed to load send preview"
      );
    } finally {
      setReceiptPreviewLoading(false);
    }
  };

  /** Send the Trademarks Receipt over WhatsApp + Email after the user confirms the preview. */
  const handleConfirmSendTrademarksReceipt = async () => {
    if (!leadId) return;
    setWhatsAppSuccess(null);
    setSendingReceipt(true);
    try {
      const result = await sendAcknowledgementTrademarksReceiptToClient(leadId);
      if (result.emailError) {
        const msg = result.email
          ? `Receipt sent via WhatsApp, but email to ${result.email} failed: ${result.emailError}`
          : `Receipt sent via WhatsApp, but email failed: ${result.emailError}`;
        setWhatsAppSuccess("Trademarks receipt sent to client via WhatsApp");
        toast.warning(msg);
      } else if (result.email) {
        setWhatsAppSuccess(
          "Trademarks receipt sent to client via WhatsApp and Email"
        );
        toast.success("Trademarks receipt sent to client via WhatsApp and Email");
      } else {
        setWhatsAppSuccess("Trademarks receipt sent to client via WhatsApp");
        toast.success(
          "Trademarks receipt sent to client via WhatsApp (no email on file)"
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSendingReceipt(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col justify-center gap-4 px-4 py-8 md:gap-5 md:px-0 md:py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-[12px] text-[#6D6D6D] md:text-sm">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col justify-center gap-4 px-4 py-8 md:gap-5 md:px-0 md:py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-[12px] text-[#6D6D6D] md:text-sm">
          Loading acknowledgement form...
        </p>
      </div>
    );
  }

  if (!formData) return null;

  const hasMultipleClasses = (formData.selectedClasses?.length ?? 0) > 1;
  const hasMultipleTypes = (formData.selectedTypesOfMark?.length ?? 0) > 1;
  const markName = formData.markName ?? "";

  /**
   * "Full data entered" gate — Preview activates as soon as Date of Filing is set and every
   * rendered application row has a TM No. We don't compare against formData.noOfApplications
   * here because the rendered `applications.length` is already initialized from it; doing both
   * checks just made the gate flicker off-and-on during initialization.
   */
  const isPreviewActive =
    !!dateOfFiling &&
    applications.length > 0 &&
    applications.every((a) => a.tmNo?.trim());

  // Format dateOfFiling to DD/MM/YYYY for display (matches summary string format).
  const dateOfFilingDisplay = (() => {
    if (summary?.dateOfFiling) return summary.dateOfFiling;
    if (!dateOfFiling) return "";
    const d = String(dateOfFiling.getDate()).padStart(2, "0");
    const m = String(dateOfFiling.getMonth() + 1).padStart(2, "0");
    return `${d}/${m}/${dateOfFiling.getFullYear()}`;
  })();

  // Effective preview values: prefer saved summary (server-truth), fall back to live form.
  const previewValues = {
    acknowledgementNo: summary?.acknowledgementNo || "Auto-generated on save",
    dateOfFiling: dateOfFilingDisplay,
    formOfAddress: summary?.formOfAddress ?? formOfAddress,
    addresseeLine1:
      summary?.addresseeLine1 ??
      formData.addresseeDefaults?.addresseeLine1 ??
      "",
    addresseeLine2:
      summary?.addresseeLine2 ??
      formData.addresseeDefaults?.addresseeLine2 ??
      "",
    addresseeLine3:
      summary?.addresseeLine3 ??
      formData.addresseeDefaults?.addresseeLine3 ??
      "",
    districtOfApplicant: summary?.districtOfApplicant ?? districtOfApplicant,
    applicant: summary?.applicant ?? formData.filingDetails?.applicant ?? "",
    markName,
    selectedClasses:
      formData.selectedClasses && formData.selectedClasses.length > 0
        ? [...formData.selectedClasses].sort((a, b) => a - b).join(", ")
        : "—",
    applications: applications.map((a) => ({
      tmNo: a.tmNo,
      classNumber: a.classNumber,
      typeOfMark: a.typeOfMark,
      mark: a.mark,
    })),
  };

  const classOptions =
    formData.selectedClasses?.map((c) => ({
      value: String(c),
      label: String(c),
    })) ?? [];
  const typeOptions =
    formData.selectedTypesOfMark?.map((t) => ({ value: t, label: t })) ?? [];

  return (
    <form
      id="acknowledgement-to-client-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      className="flex w-full max-w-[1085px] flex-col gap-4 px-4 md:gap-5 md:px-0"
      style={FORM_STYLES}
    >
      {/* Acknowledgement Details */}
      <div className="flex items-center text-base leading-5 text-[#222222] md:text-[16px]">
        Acknowledgement Details
      </div>

      {/* Row 1: Acknowledgement No. | Date of Filing */}
      <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:gap-5">
        <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
          <label className={LABEL}>Acknowledgement No.</label>
          <input
            type="text"
            placeholder="Auto-generated on save"
            value={ackNo}
            readOnly
            className={INPUT}
          />
        </div>
        <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
          <label className={LABEL}>Date of Filing</label>
          <DateTimePicker
            value={dateOfFiling}
            onChange={(d) => {
              setDateOfFiling(d);
              invalidatePdfPreview();
            }}
            placeholder="14/02/2026"
            showTime={false}
            buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs"
          />
        </div>
      </div>

      {/* Row 2: Form of Address (Auto) | District of Applicant (Auto) */}
      <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:gap-5">
        <div className="flex w-full min-w-0 flex-1 [&_button]:min-h-[44px] [&_button]:text-[12px] md:[&_button]:min-h-0 md:[&_button]:text-xs">
          <SelectField
            label="Form of Address (Auto)"
            value={formOfAddress}
            onChange={(v) => {
              setFormOfAddress(v);
              invalidatePdfPreview();
            }}
            options={FORM_OF_ADDRESS_OPTIONS}
            placeholder="Select"
          />
        </div>
        <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
          <label className={LABEL}>District of Applicant (Auto)</label>
          <input
            type="text"
            value={districtOfApplicant}
            onChange={(e) => {
              setDistrictOfApplicant(e.target.value);
              invalidatePdfPreview();
            }}
            className={INPUT}
          />
        </div>
      </div>

      {/* Filing Details — read-only fetched fields from Step 3 (Filing Details). */}
      <div className="flex items-center text-base leading-5 text-[#222222] md:text-[16px]">
        Filing Details
      </div>
      <div className="flex w-full flex-col gap-3 rounded-[10px] border border-[#EEEEEE] bg-[rgba(247,247,247,0.9)] p-3 md:p-4">
        <PreviewRow
          label="Applicant Type"
          value={formatApplicantType(formData.filingDetails?.applicantType)}
        />
        <PreviewRow
          label="Signatory's Designation"
          value={formData.filingDetails?.signatoryDesignation}
        />
        <PreviewRow
          label="Applicant"
          value={formData.filingDetails?.applicant}
        />
        <PreviewRow
          label="Applicant Address"
          value={
            [
              formData.filingDetails?.applicantAddressLine,
              formData.filingDetails?.applicantAddressDistrict,
              formData.filingDetails?.applicantAddressState,
              formData.filingDetails?.applicantAddressCountry,
            ]
              .filter((s) => s && String(s).trim())
              .join(", ") || "—"
          }
          multiline
        />
        <PreviewRow
          label="Signatory's Gender"
          value={formData.filingDetails?.signatoryGender}
        />
      </div>

      {/* Application Details */}
      <div className="flex items-center text-base leading-5 text-[#222222] md:text-[16px]">
        Application Details
      </div>

      {/* Table header row - hidden on mobile */}
      <div className="hidden h-9 min-h-[36px] flex-row items-center justify-between gap-5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 md:flex">
        <span className="flex w-8 shrink-0 items-center justify-center text-xs leading-5 text-[#000000]">
          #
        </span>
        <span className="flex w-44 shrink-0 items-center justify-center text-xs leading-5 text-[#222222]">
          TM Application No.
        </span>
        <span className="flex w-16 shrink-0 items-center justify-center text-xs leading-5 text-[#222222]">
          Class
        </span>
        <span className="flex w-24 shrink-0 items-center justify-center text-xs leading-5 text-[#222222]">
          Type
        </span>
        <span className="flex min-w-0 flex-1 items-center justify-center text-xs leading-5 text-[#222222]">
          Mark
        </span>
      </div>

      {/* Data rows - card on mobile, row on desktop */}
      {applications.map((app, idx) => (
        <div
          key={idx}
          className="md:bg-transparent flex flex-col gap-3 rounded-[6px] border border-[#EEE] bg-white p-3 md:flex-row md:items-center md:gap-5 md:border-0 md:p-0"
        >
          <div className="flex items-center gap-2 text-[12px] text-[#222222] md:w-8 md:shrink-0 md:justify-center md:text-xs">
            <span className="text-[#6d6d6d] md:hidden">#</span>
            {idx + 1}
          </div>
          <div className="flex w-full min-w-0 flex-col gap-1 md:w-44 md:shrink-0">
            <span className="text-[12px] font-normal text-[#6d6d6d] md:hidden">
              TM Application No.
            </span>
            <input
              type="text"
              placeholder="Enter TM No."
              value={app.tmNo}
              onChange={(e) => updateApplication(idx, "tmNo", e.target.value)}
              className={INPUT}
            />
          </div>
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-1 md:w-16">
            <span className="text-[12px] font-normal text-[#6d6d6d] md:hidden">
              Class
            </span>
            <div className={`${INPUT} flex items-center`}>
              {app.classNumber || "—"}
            </div>
          </div>
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-1 md:w-24">
            <span className="text-[12px] font-normal text-[#6d6d6d] md:hidden">
              Type
            </span>
            <div className={`${INPUT} flex items-center`}>
              {app.typeOfMark || "—"}
            </div>
          </div>
          <div className="flex w-full min-w-0 flex-col gap-1 md:flex-1">
            <span className="text-[12px] font-normal text-[#6d6d6d] md:hidden">
              Mark
            </span>
            <div
              className={`${INPUT} flex items-center truncate`}
              title={app.mark || markName || "Word mark"}
            >
              {app.mark || markName || "Word mark"}
            </div>
          </div>
        </div>
      ))}

      {/*
       * In-page Save + Preview buttons. Save persists the form without advancing to the next
       * step — needed so the user can keep working on this page (Preview, document uploads,
       * Send to Client). The parent footer's "Save & Continue" still saves AND advances.
       */}
      <div className="flex flex-col flex-wrap items-stretch justify-center gap-2.5 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => {
            void handleSave({ advance: false });
          }}
          disabled={saving || !isPreviewActive}
          title={
            !isPreviewActive
              ? "Enter Date of Filing and every TM No. to enable Save"
              : summary
              ? "Save changes without leaving this step"
              : "Save without leaving this step (use Save & Continue to advance)"
          }
          className={`flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-[6px] bg-[#222222] px-5 py-2.5 text-[12px] font-medium leading-5 text-white transition-opacity hover:bg-[#333333] sm:h-9 sm:py-2 md:text-xs ${
            !isPreviewActive
              ? "cursor-not-allowed opacity-50"
              : saving
              ? "cursor-wait"
              : ""
          }`}
        >
          {saving && (
            <svg
              className="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.25"
              />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          )}
          {saving ? "Saving…" : summary ? "Save changes" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => {
            setPreviewModalOpen(true);
            setActiveTab("data");
          }}
          disabled={!isPreviewActive}
          title={
            !isPreviewActive
              ? "Enter Date of Filing and every TM No. to enable preview"
              : undefined
          }
          className={`flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-[6px] border border-[#222222] bg-white px-5 py-2.5 text-[12px] font-medium leading-5 text-[#222222] transition-opacity hover:bg-[#F7F7F7] sm:h-9 sm:py-2 md:text-xs ${
            !isPreviewActive ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <img src={pdfIcon} alt="" className="h-[18px] w-[18px] shrink-0" />
          Preview
        </button>
        <button
          type="button"
          onClick={async () => {
            const updated = await handleGeneratePdf();
            if (updated?.acknowledgementLetterPdfUrl) {
              setPreviewPdfUrl(updated.acknowledgementLetterPdfUrl);
              setNumPages(null);
            }
          }}
          disabled={generatingPdf || saving || !isPreviewActive}
          title={
            !isPreviewActive
              ? "Enter Date of Filing and every TM No. to enable Generate PDF"
              : undefined
          }
          className={`flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-[#fcfcfc] px-4 py-2.5 text-[12px] font-medium leading-5 text-[#222222] transition-opacity hover:bg-[#F7F7F7] sm:h-9 sm:py-2 md:text-xs ${
            !isPreviewActive ? "cursor-not-allowed opacity-50" : ""
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <img src={pdfIcon} alt="" className="h-[18px] w-[18px] shrink-0" />
          {generatingPdf || saving
            ? "Generating…"
            : summary?.acknowledgementLetterPdfKey
            ? "Regenerate PDF"
            : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={() => {
            void handleDownloadPdf();
          }}
          disabled={downloadingPdf || !summary?.acknowledgementLetterPdfUrl}
          className="min-h-[44px] touch-manipulation rounded-[6px] bg-[#222222] px-4 py-2.5 text-center text-[12px] font-medium leading-5 text-white hover:bg-[#333333] disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:py-2 md:text-xs"
        >
          {downloadingPdf ? "Downloading…" : "Download"}
        </button>
        {!isPreviewActive && (
          <span className="self-center text-[11px] text-[#6D6D6D] md:text-xs">
            Awaiting full data — fill Date of Filing and every TM No.
          </span>
        )}
        {/* Parent footer's Save & Continue saves AND advances to the next step */}
      </div>

      {/* Documents section - upload reports, merge PDFs, manage workflow */}
      {summary && (
        <div className="mt-2 flex flex-col gap-4">
          <div className="flex items-center text-base leading-5 text-[#222222] md:text-[16px]">
            Documents
          </div>

          {/* Application Status PDFs (one per TM No.) */}
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col gap-0.5">
              <p className="text-[12px] font-medium text-[#222222] md:text-xs">
                Application Status PDFs
              </p>
              <p className="text-[11px] text-[#6D6D6D] md:text-[11px]">
                Upload one PDF per TM No. Required before merging the letter
                with status.
              </p>
            </div>
            {(summary.applicationStatusPdfs ?? []).map((app) => {
              const uploaded = !!app.pdfKey;
              const uploading = !!statusUploading[app.ackApplicationId];
              const inputId = `ack-status-pdf-${app.ackApplicationId}`;
              return (
                <div
                  key={app.ackApplicationId}
                  className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3"
                >
                  <span className="shrink-0 truncate text-[12px] text-[#222222] sm:w-32 md:text-xs">
                    {app.tmNo || "—"}
                  </span>
                  <span
                    className={`min-w-0 flex-1 truncate text-[12px] md:text-xs ${
                      uploaded ? "text-[#16A34A]" : "text-[#6D6D6D]"
                    }`}
                  >
                    {uploading
                      ? "Uploading…"
                      : uploaded
                      ? "PDF uploaded"
                      : "No PDF uploaded yet"}
                  </span>
                  <div className="flex items-center gap-2">
                    {uploaded && app.pdfUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setFilePreview({
                            url: app.pdfUrl!,
                            name: `Application Status - ${app.tmNo}.pdf`,
                          })
                        }
                        className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] md:text-xs`}
                      >
                        View
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => document.getElementById(inputId)?.click()}
                      className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] disabled:opacity-50 md:text-xs`}
                    >
                      {uploading
                        ? "Uploading…"
                        : uploaded
                        ? "Replace"
                        : "Upload"}
                    </button>
                    <input
                      id={inputId}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f)
                          handleUploadStatusPdf(
                            app.ackApplicationId,
                            app.tmNo,
                            f
                          );
                        e.target.value = "";
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trademarks Receipt + Acknowledgement Letter override */}
          <div className="flex flex-col gap-2.5 md:flex-row md:gap-4">
            {/* Trademarks Receipt */}
            <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-3 py-2.5">
              <div className="flex flex-col gap-0.5">
                <p className="text-[12px] font-medium text-[#222222] md:text-xs">
                  Trademarks Receipt
                </p>
                <p className="text-[11px] text-[#6D6D6D]">
                  Upload the receipt PDF received from the registry.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`min-w-0 flex-1 truncate text-[12px] md:text-xs ${
                    summary.trademarksReceiptPdfKey
                      ? "text-[#16A34A]"
                      : "text-[#6D6D6D]"
                  }`}
                >
                  {receiptUploading
                    ? "Uploading…"
                    : summary.trademarksReceiptPdfKey
                    ? "Receipt uploaded"
                    : "No receipt uploaded yet"}
                </span>
                {summary.trademarksReceiptPdfUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      setFilePreview({
                        url: summary.trademarksReceiptPdfUrl!,
                        name: "Trademarks Receipt.pdf",
                      })
                    }
                    className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] md:text-xs`}
                  >
                    View
                  </button>
                )}
                <button
                  type="button"
                  disabled={receiptUploading}
                  onClick={() =>
                    document.getElementById("ack-receipt-input")?.click()
                  }
                  className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] disabled:opacity-50 md:text-xs`}
                >
                  {receiptUploading
                    ? "Uploading…"
                    : summary.trademarksReceiptPdfKey
                    ? "Replace"
                    : "Upload"}
                </button>
                <input
                  id="ack-receipt-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadTrademarksReceipt(f);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>

            {/* Acknowledgement Letter (override) */}
            <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-3 py-2.5">
              <div className="flex flex-col gap-0.5">
                <p className="text-[12px] font-medium text-[#222222] md:text-xs">
                  Acknowledgement Letter
                </p>
                <p className="text-[11px] text-[#6D6D6D]">
                  Auto-generated via Preview. Upload to override the generated
                  PDF.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`min-w-0 flex-1 truncate text-[12px] md:text-xs ${
                    summary.acknowledgementLetterPdfKey
                      ? "text-[#16A34A]"
                      : "text-[#6D6D6D]"
                  }`}
                >
                  {letterUploading
                    ? "Uploading…"
                    : summary.acknowledgementLetterPdfKey
                    ? "Letter available"
                    : "No letter generated yet"}
                </span>
                {summary.acknowledgementLetterPdfUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      setFilePreview({
                        url: summary.acknowledgementLetterPdfUrl!,
                        name: "Acknowledgement Letter.pdf",
                      })
                    }
                    className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] md:text-xs`}
                  >
                    View
                  </button>
                )}
                <button
                  type="button"
                  disabled={letterUploading}
                  onClick={() =>
                    document.getElementById("ack-letter-input")?.click()
                  }
                  className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] disabled:opacity-50 md:text-xs`}
                >
                  {letterUploading ? "Uploading…" : "Override"}
                </button>
                <input
                  id="ack-letter-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadMergedLetter(f);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          </div>

          {/* Merge Letter + Status PDFs */}
          <div className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-3 py-2.5">
            <div className="flex flex-col gap-0.5">
              <p className="text-[12px] font-medium text-[#222222] md:text-xs">
                Merged Letter + Status PDF
              </p>
              <p className="text-[11px] text-[#6D6D6D]">
                Concatenates the Acknowledgement Letter with each Application
                Status PDF in TM No. order. This is the file forwarded to the
                client.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span
                className={`min-w-0 flex-1 truncate text-[12px] md:text-xs ${
                  summary.acknowledgementLetterWithStatusPdfKey
                    ? "text-[#16A34A]"
                    : "text-[#6D6D6D]"
                }`}
              >
                {merging
                  ? "Generating…"
                  : summary.acknowledgementLetterWithStatusPdfKey
                  ? "Merged PDF ready"
                  : "Not generated yet"}
              </span>
              {summary.acknowledgementLetterWithStatusPdfUrl && (
                <button
                  type="button"
                  onClick={() =>
                    setFilePreview({
                      url: summary.acknowledgementLetterWithStatusPdfUrl!,
                      name: "Merged Letter + Status PDF.pdf",
                    })
                  }
                  className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center !px-3 !py-1.5 text-[12px] hover:bg-[#EEEEEE] md:text-xs`}
                >
                  View
                </button>
              )}
              <button
                type="button"
                onClick={handleMergeWithStatus}
                disabled={merging}
                className={`${INPUT_GHOST} flex !h-9 !min-h-[36px] cursor-pointer items-center justify-center whitespace-nowrap !px-4 !py-1.5 text-[12px] hover:bg-[#EEEEEE] disabled:opacity-50 md:text-xs`}
              >
                {merging
                  ? "Generating…"
                  : summary.acknowledgementLetterWithStatusPdfKey
                  ? "Regenerate"
                  : "Generate Merged PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp section - after save */}
      {summary && (
        <div className="mt-2 flex flex-col gap-3">
          <div className="flex items-center text-base leading-5 text-[#222222] md:text-[16px]">
            Send to Client
          </div>
          <div className="flex flex-col flex-wrap items-stretch gap-2.5 sm:flex-row sm:items-center md:gap-4">
            <input
              type="tel"
              inputMode="numeric"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(formatPhoneInput(e.target.value))}
              placeholder="e.g. +919876543210 or 9876543210"
              className="min-h-[44px] w-full min-w-0 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] text-[#000000] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] sm:w-[200px] md:h-9 md:text-xs"
            />
            <button
              type="button"
              onClick={handleSendMergedLetter}
              disabled={
                sendingMerged ||
                mergedPreviewLoading ||
                !summary.acknowledgementLetterWithStatusPdfKey
              }
              title={
                !summary.acknowledgementLetterWithStatusPdfKey
                  ? "Generate the Merged Letter + Status PDF first"
                  : "Sends the combined Letter + Status PDF via WhatsApp and Email"
              }
              className={`${INPUT_GHOST} flex w-full cursor-pointer touch-manipulation items-center justify-center whitespace-nowrap transition-colors hover:bg-[#EEEEEE] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[260px]`}
            >
              {sendingMerged
                ? "Sending…"
                : mergedPreviewLoading
                ? "Loading preview…"
                : "Send Merged Letter (WhatsApp + Email)"}
            </button>
            <button
              type="button"
              onClick={handleSendTrademarksReceipt}
              disabled={
                sendingReceipt ||
                receiptPreviewLoading ||
                !summary.trademarksReceiptPdfKey
              }
              title={
                !summary.trademarksReceiptPdfKey
                  ? "Upload Trademarks Receipt first"
                  : "Sends to the client via WhatsApp and Email"
              }
              className={`${INPUT_GHOST} flex w-full cursor-pointer touch-manipulation items-center justify-center whitespace-nowrap transition-colors hover:bg-[#EEEEEE] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[260px]`}
            >
              {sendingReceipt
                ? "Sending…"
                : receiptPreviewLoading
                ? "Loading preview…"
                : "Send Trademarks Receipt (WhatsApp + Email)"}
            </button>
          </div>
          {whatsAppSuccess && (
            <p className="text-xs text-green-600">{whatsAppSuccess}</p>
          )}
        </div>
      )}

      {/* Merged Letter — confirm WhatsApp + Email content before sending */}
      <ConfirmMessageModal
        open={showMergedConfirm}
        onClose={() => setShowMergedConfirm(false)}
        title="Send Merged Letter to client"
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (with combined Letter + Status PDF link)
              </p>
              <WhatsAppMessageBlock
                message={mergedWaMessage}
                loading={mergedPreviewLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with combined Letter + Status PDF)
              </p>
              <EmailPreviewBlock
                preview={mergedEmailPreview}
                loading={mergedPreviewLoading}
                attachmentFallback={
                  summary?.acknowledgementLetterWithStatusPdfUrl
                    ? {
                        filename: "Merged Letter + Status PDF.pdf",
                        url: summary.acknowledgementLetterWithStatusPdfUrl,
                      }
                    : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => {
          void handleConfirmSendMergedLetter();
        }}
        disableConfirm={sendingMerged || mergedPreviewLoading}
      />

      {/* Trademarks Receipt — confirm WhatsApp + Email content before sending */}
      <ConfirmMessageModal
        open={showReceiptConfirm}
        onClose={() => setShowReceiptConfirm(false)}
        title="Send Trademarks Receipt to client"
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (with receipt PDF link)
              </p>
              <WhatsAppMessageBlock
                message={receiptWaMessage}
                loading={receiptPreviewLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with receipt PDF)
              </p>
              <EmailPreviewBlock
                preview={receiptEmailPreview}
                loading={receiptPreviewLoading}
                attachmentFallback={
                  summary?.trademarksReceiptPdfUrl
                    ? {
                        filename: "Trademarks Receipt.pdf",
                        url: summary.trademarksReceiptPdfUrl,
                      }
                    : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => {
          void handleConfirmSendTrademarksReceipt();
        }}
        disableConfirm={sendingReceipt || receiptPreviewLoading}
      />

      {/* Preview popup — two tabs (Data | PDF). Data tab is rendered live from form state;
          PDF tab lazy-loads (and generates if necessary) the Acknowledgement Letter PDF. */}
      <FilePreviewModal
        open={!!filePreview}
        onClose={() => setFilePreview(null)}
        name={filePreview?.name ?? ""}
        url={filePreview?.url}
        kind="pdf"
      />

      {previewModalOpen && (
        <div className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div
            className="relative flex h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:h-[92vh]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span className="text-xs font-medium text-[#222] sm:text-sm">
                Acknowledgement Letter — Preview
              </span>
              <button
                type="button"
                onClick={() => {
                  setPreviewModalOpen(false);
                  setActiveTab("data");
                }}
                className="flex size-10 shrink-0 touch-manipulation items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>

            {/* Tab nav */}
            <div className="flex shrink-0 items-center gap-1 border-b border-[#e3e3e3] px-3 pt-2 sm:px-4">
              <button
                type="button"
                onClick={() => setActiveTab("data")}
                className={`rounded-t-md border-b-2 px-4 py-2 text-[12px] font-medium transition-colors md:text-xs ${
                  activeTab === "data"
                    ? "border-[#222222] bg-[#fafafa] text-[#222222]"
                    : "border-transparent text-[#6D6D6D] hover:text-[#222]"
                }`}
              >
                Data
              </button>
              <button
                type="button"
                onClick={async () => {
                  setActiveTab("pdf");
                  if (!previewPdfUrl && !previewing && leadId) {
                    setPreviewing(true);
                    try {
                      // Always save first so any unsaved edits (e.g. a changed Date of Filing)
                      // reach the DB before generation. Backend hash-cache short-circuits the
                      // generate call when nothing actually changed, so this is cheap on no-ops.
                      const currentSummary = await handleSave({
                        advance: false,
                      });
                      if (!currentSummary) return;
                      const result = await generateAcknowledgementLetter(
                        leadId
                      );
                      const updated = await getAcknowledgementSummary(leadId);
                      if (updated) {
                        setSummary(updated);
                        applySummary(updated);
                      }
                      if (result.downloadUrl)
                        setPreviewPdfUrl(result.downloadUrl);
                    } catch (err) {
                      toast.error(
                        err instanceof Error
                          ? err.message
                          : "Failed to load PDF"
                      );
                    } finally {
                      setPreviewing(false);
                    }
                  }
                }}
                className={`rounded-t-md border-b-2 px-4 py-2 text-[12px] font-medium transition-colors md:text-xs ${
                  activeTab === "pdf"
                    ? "border-[#222222] bg-[#fafafa] text-[#222222]"
                    : "border-transparent text-[#6D6D6D] hover:text-[#222]"
                }`}
              >
                PDF
              </button>
            </div>

            {/* Tab body */}
            <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
              {activeTab === "data" ? (
                <div className="flex w-full flex-col gap-3 rounded-[10px] border border-[#EEEEEE] bg-[rgba(247,247,247,0.9)] p-3 md:p-4">
                  <PreviewRow
                    label="Acknowledgement No."
                    value={previewValues.acknowledgementNo}
                  />
                  <PreviewRow
                    label="Date of Filing"
                    value={previewValues.dateOfFiling}
                  />
                  <PreviewRow
                    label="Form of Address"
                    value={previewValues.formOfAddress}
                  />
                  <PreviewRow
                    label="Addressee"
                    value={
                      [
                        previewValues.addresseeLine1,
                        previewValues.addresseeLine2,
                        previewValues.addresseeLine3,
                      ]
                        .filter((s) => s && String(s).trim())
                        .join("\n") || "—"
                    }
                    multiline
                  />
                  <PreviewRow
                    label="District of Applicant"
                    value={previewValues.districtOfApplicant}
                  />
                  <PreviewRow
                    label="Applicant"
                    value={previewValues.applicant}
                  />
                  <PreviewRow label="Mark" value={previewValues.markName} />
                  <PreviewRow
                    label="Selected Classes"
                    value={previewValues.selectedClasses}
                  />
                  <PreviewRow
                    label="No. of Applications"
                    value={String(previewValues.applications.length)}
                  />

                  {previewValues.applications.length > 0 && (
                    <div className="mt-1 flex flex-col gap-2">
                      <span className="text-[12px] text-[#6D6D6D] md:text-xs">
                        Applications
                      </span>
                      <div className="flex flex-col overflow-hidden rounded-[6px] border border-[#EEEEEE] bg-white">
                        <div className="hidden grid-cols-[40px_1fr_60px_100px_1fr] gap-3 bg-[#F7F7F7] px-3 py-2 text-[11px] font-medium text-[#6D6D6D] md:grid">
                          <span>#</span>
                          <span>TM No.</span>
                          <span>Class</span>
                          <span>Type</span>
                          <span>Mark</span>
                        </div>
                        {previewValues.applications.map((a, i) => (
                          <div
                            key={`${a.tmNo}-${i}`}
                            className="flex flex-col gap-1 border-t border-[#F0F0F0] px-3 py-2 text-[12px] text-[#222222] md:grid md:grid-cols-[40px_1fr_60px_100px_1fr] md:flex-row md:gap-3 md:text-xs"
                          >
                            <span className="text-[#6D6D6D] md:text-[#222222]">
                              <span className="md:hidden">#</span> {i + 1}
                            </span>
                            <span className="truncate" title={a.tmNo}>
                              <span className="text-[#6D6D6D] md:hidden">
                                TM No.:{" "}
                              </span>
                              {a.tmNo || "—"}
                            </span>
                            <span>
                              <span className="text-[#6D6D6D] md:hidden">
                                Class:{" "}
                              </span>
                              {a.classNumber || "—"}
                            </span>
                            <span className="truncate" title={a.typeOfMark}>
                              <span className="text-[#6D6D6D] md:hidden">
                                Type:{" "}
                              </span>
                              {a.typeOfMark || "—"}
                            </span>
                            <span className="truncate" title={a.mark}>
                              <span className="text-[#6D6D6D] md:hidden">
                                Mark:{" "}
                              </span>
                              {a.mark || previewValues.markName || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* PDF tab */
                <div className="flex flex-col gap-3">
                  {/* PDF viewer */}
                  {!summary && !previewing ? (
                    <p className="py-8 text-center text-[12px] text-[#6D6D6D] md:text-xs">
                      Click <strong>Generate PDF</strong> to save the form and
                      create the letter.
                    </p>
                  ) : previewing && !previewPdfUrl ? (
                    <p className="py-8 text-center text-[12px] text-[#6D6D6D] md:text-xs">
                      Loading PDF…
                    </p>
                  ) : !previewPdfUrl ? (
                    <p className="py-8 text-center text-[12px] text-[#6D6D6D] md:text-xs">
                      PDF not generated yet. Click <strong>Generate PDF</strong>{" "}
                      to create it.
                    </p>
                  ) : (
                    <Document
                      file={previewPdfUrl}
                      onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                      loading={
                        <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                      }
                      error={
                        <p className="text-sm text-red-600">
                          Failed to load PDF.
                        </p>
                      }
                      className="flex flex-col items-center gap-4"
                    >
                      {numPages != null &&
                        Array.from({ length: numPages }, (_, i) => (
                          <Page
                            key={i + 1}
                            pageNumber={i + 1}
                            width={Math.min(
                              760,
                              typeof window !== "undefined"
                                ? window.innerWidth - 48
                                : 760
                            )}
                            renderTextLayer
                            renderAnnotationLayer
                          />
                        ))}
                    </Document>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
